import React, { useEffect, useRef } from 'react';
import { Banner, BlockStack, Text, InlineStack } from '@shopify/polaris';
import { useTierConfig } from '../providers/TierConfigProvider';

/**
 * T058 + T102: UpgradeBanner component with analytics event tracking (SC-012).
 * Polaris Banner with "Upgrade to see all suggestions" CTA and plan comparison.
 * Tracks impression (on mount) and click-through events.
 */

/** Analytics event tracker — fires events to the hosting app or console */
function trackEvent(eventName: string, payload: Record<string, unknown>): void {
  try {
    // Use Shopify App Bridge analytics if available
    if (typeof window !== 'undefined' && (window as any).__SHOPIFY_DEV_HOST) {
      // In production, this would use App Bridge's analytics
      console.info(`[Analytics] ${eventName}`, payload);
    }

    // Also dispatch a custom event for any external analytics listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('zelium:analytics', {
          detail: { event: eventName, ...payload },
        }),
      );
    }
  } catch {
    // Analytics should never break the UI
  }
}

interface UpgradeBannerProps {
  visibleCount: number;
  totalCount: number;
  feature?: string;
}

export function UpgradeBanner({
  visibleCount,
  totalCount,
  feature = 'suggestions',
}: UpgradeBannerProps): React.ReactElement | null {
  const hiddenCount = totalCount - visibleCount;
  const tracked = useRef(false);
  const { getLimits } = useTierConfig();

  const freeSuggestions = getLimits('FREE')?.maxSuggestionsPerProduct ?? 3;
  const freeProducts = getLimits('FREE')?.productLimit ?? 25;
  const starterSuggestions = getLimits('STARTER')?.maxSuggestionsPerProduct ?? 20;
  const starterProducts = getLimits('STARTER')?.productLimit ?? 100;

  // T102: Track impression on mount (SC-012)
  useEffect(() => {
    if (hiddenCount > 0 && !tracked.current) {
      tracked.current = true;
      trackEvent('upgrade_banner_impression', {
        feature,
        visibleCount,
        totalCount,
        hiddenCount,
      });
    }
  }, [hiddenCount, feature, visibleCount, totalCount]);

  if (hiddenCount <= 0) return null;

  const handleViewPlansClick = () => {
    // T102: Track click-through (SC-012)
    trackEvent('upgrade_banner_click', {
      feature,
      visibleCount,
      totalCount,
      hiddenCount,
    });
  };

  return (
    <Banner
      title={`${hiddenCount} more ${feature} available`}
      tone="info"
      action={{
        content: 'View plans',
        url: '/billing',
        onAction: handleViewPlansClick,
      }}
    >
      <BlockStack gap="200">
        <Text as="p" variant="bodySm">
          Upgrade to unlock all {totalCount} {feature} and get higher scan limits, extended score history,
          and priority support.
        </Text>
        <InlineStack gap="400">
          <BlockStack gap="100">
            <Text as="span" variant="bodySm" fontWeight="semibold">
              Free
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {freeSuggestions} suggestions · {freeProducts} products · 1 scan/day
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text as="span" variant="bodySm" fontWeight="semibold">
              Starter
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {starterSuggestions} suggestions · {starterProducts} products · 5 scans/day
            </Text>
          </BlockStack>
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}
